import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.facebookApiUrl, {
  camelizeKeys: true,
});

export const oldHttp = createDashHttpClient(env.facebookApiUrl);

// Facebook often returns an after cursor item when it should not
const correctPagingCursor = (response) => {
  const { data } = response.data;
  if (data?.length === 0) {
    response.data.paging.cursors.after = null;
  }
  return response;
};

export async function getFacebookPage({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/page`, config);
}

export async function getPages({ ids, q, limit }, config = {}) {
  return http.get(
    `/pages`,
    merge(
      {
        params: {
          ids,
          q,
          limit,
        },
      },
      config,
    ),
  );
}

export async function getReport360Posts({ brandId, month }, config = {}) {
  return oldHttp.get(
    `/brands/${brandId}/facebook/reports/360/posts`,
    merge({ params: { month } }, config),
  );
}

export default {
  async getBusinesses({ brandId, params }, config = {}) {
    const url = `/brands/${brandId}/fb_businesses`;
    return oldHttp.get(url, { ...config, params });
  },
  async getBusinessCatalogs({ brandId, params, paging = {} }, config = {}) {
    const localParams = { ...params };
    const after = paging?.cursors?.after;
    if (after) {
      localParams.after = after;
    }

    const url = `/brands/${brandId}/fb_business_catalogs`;
    const res = await oldHttp.get(url, { ...config, params: localParams });
    return correctPagingCursor(res);
  },
  async getPageMetrics({ brandId, startDate, endDate, scale }) {
    return oldHttp.get(`/brands/${brandId}/page/metrics`, {
      params: {
        startDate,
        endDate,
        scale,
      },
    });
  },
  async getCampaignList({ brandId }) {
    return oldHttp.get(`/brands/${brandId}/campaigns`);
  },
  async getAccountsFromApi(
    { brandId, fetchApi, connectFacebookAdsToken, isMonitored },
    config = {},
  ) {
    return oldHttp.get(
      `/brands/${brandId}/ad_accounts`,
      merge(
        {
          params: {
            fetchApi,
            connectFacebookAdsToken,
            isMonitored,
          },
        },
        config,
      ),
    );
  },
  async getAccountsFromApiV2({ brandId, connectFacebookAdsToken }, config = {}) {
    return oldHttp.get(
      `/brands/${brandId}/ad_accounts/v2`,
      merge(
        {
          params: {
            connectFacebookAdsToken,
          },
        },
        config,
      ),
    );
  },
  async getAccountsMultiBrandsFromApi({ brandIds }, config = {}) {
    return oldHttp.put('/brands/ad_accounts', { brand_ids: brandIds }, config);
  },
  async getAllFacebookPages({ brandIds }, config = {}) {
    return http.put('/brands/pages', { brand_ids: brandIds }, config);
  },
  async updateAccount({ brandId, fbAccountId, isMonitored }) {
    return oldHttp.patch(`/brands/${brandId}/ad_accounts/${fbAccountId}`, {
      is_monitored: isMonitored,
    });
  },
  async deleteAccount({ brandId, fbAccountId }) {
    return oldHttp.delete(`/brands/${brandId}/ad_accounts/${fbAccountId}`);
  },
  async getPageFollowersCSV({ brandId, startDate, endDate, filename, scale }) {
    return oldHttp.get(`/brands/${brandId}/exports/page_followers`, {
      params: {
        startDate,
        endDate,
        filename,
        scale,
      },
    });
  },
  instagramProductTags: {
    async searchProducts(
      { brandId, params: { instagramUserId, query, catalogId, after } = {} },
      config = {},
    ) {
      const url = `/brands/${brandId}/instagram_product_tags/search_products`;
      return http.get(url, {
        ...config,
        params: { instagramUserId, query, catalogId, after },
      });
    },
    async searchProductsByIds({ brandId, params: { productIds } = {} }, config = {}) {
      const url = `/brands/${brandId}/instagram_product_tags/products`;
      return http.get(url, {
        ...config,
        params: { productIds: productIds.join(',') },
      });
    },
  },
  async getAccessToken({ brandId, requiredType = 'user', requiredScopes = [] }, config = {}) {
    return http.get('/access_tokens', {
      ...config,
      params: { brandId, requiredType, requiredScopes: requiredScopes.join(',') },
    });
  },
};

export async function getInstagramProductTagsEligibility({ brandId }, config = {}) {
  return oldHttp.get(`/brands/${brandId}/instagram_product_tags/eligibility`, config);
}

export async function getInstagramProductTagsCatalogs({ brandId, instagramUserId }, config = {}) {
  return oldHttp.get(
    `/brands/${brandId}/instagram_product_tags/catalogs`,
    merge({ params: { instagramUserId } }, config),
  );
}

export const collateData = (currentValue, newValue, { uniqueOn, faultyNext = false } = {}) => {
  if ((currentValue ?? []).length === 0) {
    return newValue;
  }

  // Some facebook endpoints return a next when the enxt request returns an empty array
  if (!faultyNext && (newValue ?? []).length === 0) {
    return [];
  }

  // Facebook paging cannot be trusted not to have duplicates
  const combined = [...currentValue, ...newValue];
  if (!uniqueOn) {
    return combined;
  }

  const uniqueIdentifiers = new Set(combined.map((obj) => obj[uniqueOn]));
  return Array.from(uniqueIdentifiers).map((id) => combined.find((obj) => obj[uniqueOn] === id));
};

export async function getLocations(
  { brandId, query, locationType, locationFields, limit },
  config = {},
) {
  return oldHttp.get(
    `/locations`,
    merge(
      {
        params: {
          brandId,
          query,
          locationType,
          locationFields,
          limit,
        },
      },
      config,
    ),
  );
}

export async function getMarketingV2Campaigns(
  { adAccountIds, brandIds, sourceCampaignIds },
  config = {},
) {
  return http.put(
    `/brands/marketing_v2/campaigns`,
    {
      adAccountIds,
      brandIds,
      sourceCampaignIds,
    },
    merge({}, config),
  );
}

export async function searchMarketingV2Campaigns(
  {
    brandId,
    sourceAdAccountIds,
    effectiveStatuses,
    limit,
    offset,
    sort,
    search,
    startDate,
    endDate,
  },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/marketing_v2/campaigns/search`,
    {
      source_ad_account_ids: sourceAdAccountIds,
      effectiveStatuses,
      limit,
      offset,
      sort,
      search,
      start_date: startDate,
      end_date: endDate,
    },
    merge({}, config),
  );
}

export async function getMarketingV2Ads(
  {
    brandId,
    sourceCampaignIds,
    sourceAdIds,
    sort,
    search = /** @type {string | undefined} */ (undefined),
    effectiveStatuses = /** @type {string[] | undefined} */ (undefined),
    creativeTypes = /** @type {string[] | undefined} */ (undefined),
    limit = 100,
    offset = 0,
  },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/marketing_v2/ads/search`,
    {
      sourceCampaignIds,
      sourceAdIds,
      sort,
      search,
      effectiveStatuses,
      creativeTypes,
      limit,
      offset,
    },
    merge({}, config),
  );
}

export async function createMarketingV2Ads({ brandId, sourceAdAccountId, ads }, config = {}) {
  return http.post(
    `/brands/${brandId}/marketing_v2/ads`,
    {
      sourceAdAccountId,
      ads,
    },
    merge({}, config),
  );
}

export async function sendImagesToMetaAds({ brandId, adAccountIds, imageUrls }, config = {}) {
  return http.post(
    `/brands/${brandId}/marketing_v2/adimages`,
    {
      adAccountIds,
      imageUrls,
    },
    merge({}, config),
  );
}

export async function getMetaOpportunityScore({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/meta_opportunity_score`, merge({}, config));
}
